import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import MediumSelector from '../components/Onboarding/MediumSelector';
import ReadinessSelector from '../components/Onboarding/ReadinessSelector';
import SeriesQuestion from '../components/Onboarding/SeriesQuestion';
import BasicInfoForm from '../components/Onboarding/BasicInfoForm';
import TemplatePicker from '../components/Sessions/TemplatePicker';
import SessionNamingStep from '../components/Sessions/SessionNamingStep';
import { savePreferences, completeOnboarding } from '../utils/preferences';
import { onboardingApi } from '../api/onboarding';
import { templatesApi } from '../api/templates';
import '../components/Onboarding/Onboarding.css';

/**
 * Main onboarding page that orchestrates the multi-step flow.
 * Steps: medium -> readiness -> series -> (optional) info -> template -> naming
 */
function OnboardingPage() {
  const navigate = useNavigate();
  const { completeOnboardingFlow } = useApp();
  const { isDark, toggleTheme } = useTheme();

  const [step, setStep] = useState('medium'); // 'medium' | 'readiness' | 'series' | 'info' | 'template' | 'naming'
  const [medium, setMedium] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [isSeries, setIsSeries] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Template/session state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Architect path info (stored for later use)
  const [architectInfo, setArchitectInfo] = useState(null);

  // Load templates on mount
  useEffect(() => {
    templatesApi.list()
      .then(response => {
        setTemplates(response.data || []);
      })
      .catch(err => {
        console.error('Failed to load templates:', err);
      });
  }, []);

  /**
   * Handle medium selection (Novel/Film).
   */
  const handleMediumSelect = (selectedMedium) => {
    setMedium(selectedMedium);
    setStep('readiness');
  };

  /**
   * Handle readiness slider selection.
   * All paths now go to series question first.
   */
  const handleReadinessSelect = (selectedReadiness) => {
    setReadiness(selectedReadiness);
    setStep('series');
  };

  /**
   * Handle series question selection. Routes to the architect info form
   * (high readiness) or straight to the template picker (explorer path).
   */
  const handleSeriesSelect = ({ isSeries: selectedIsSeries }) => {
    setIsSeries(selectedIsSeries);
    if (readiness === 'formed') {
      setStep('info');
    } else {
      setStep('template');
    }
  };

  /**
   * Handle basic info form submission (architect path).
   * Store info and proceed to template picker.
   */
  const handleInfoSubmit = (info) => {
    setArchitectInfo(info);
    setStep('template');
  };

  /**
   * Handle template selection.
   */
  const handleTemplateSelect = (templateKey) => {
    const template = templates.find(t => t.key === templateKey);
    setSelectedTemplate(template);
    setStep('naming');
  };

  /**
   * Handle session naming and create the structure.
   */
  const handleSessionNameSubmit = (sessionName) => {
    if (architectInfo) {
      // Architect path - create explicit structure
      createExplicitStructureAndNavigate(architectInfo, sessionName, selectedTemplate?.key);
    } else {
      // Explorer path - create implicit structure
      createImplicitStructureAndNavigate(sessionName, selectedTemplate?.key);
    }
  };

  /**
   * Create implicit structure for explorer path.
   * Creates project/story/session with user-selected template and name.
   */
  const createImplicitStructureAndNavigate = async (sessionName, templateKey) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await onboardingApi.createImplicitStructure(
        medium,
        readiness,
        isSeries,
        null, // series_name
        sessionName,
        templateKey
      );

      // Save preferences
      savePreferences({ medium, workingStyle: 'explorer' });
      completeOnboarding();

      // Update context if function exists
      if (completeOnboardingFlow) {
        completeOnboardingFlow(medium, 'explorer');
      }

      // Series projects navigate to Discovery; standalone to chat
      if (result.is_series && result.project_id) {
        navigate(`/projects/${result.project_id}/discovery`);
      } else {
        navigate(`/chat/${result.session_id}`);
      }
    } catch (err) {
      console.error('Onboarding error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Create explicit structure for architect path.
   * Creates named project/story/session with provided info and user-selected template/name.
   */
  const createExplicitStructureAndNavigate = async (info, sessionName, templateKey) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await onboardingApi.createExplicitStructure({
        medium: info.medium,
        title: info.title,
        pitch: info.pitch,
        is_series: info.isSeries,
        readiness: readiness,
        session_name: sessionName,
        template_key: templateKey,
      });

      // Save preferences
      savePreferences({
        medium: info.medium,
        workingStyle: 'architect',
        lastProject: { title: info.title }
      });
      completeOnboarding();

      // Update context if function exists
      if (completeOnboardingFlow) {
        completeOnboardingFlow(info.medium, 'architect');
      }

      // Series projects navigate to Discovery; standalone to chat
      if (result.is_series && result.project_id) {
        navigate(`/projects/${result.project_id}/discovery`);
      } else {
        navigate(`/chat/${result.session_id}`, {
          state: { openingMessage: result.opening_message }
        });
      }
    } catch (err) {
      console.error('Onboarding error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Go back to previous step.
   */
  const handleBack = () => {
    if (step === 'naming') {
      setStep('template');
      setSelectedTemplate(null);
    } else if (step === 'template') {
      // Go back to info (architect) or series (explorer)
      if (architectInfo) {
        setStep('info');
      } else {
        setStep('series');
      }
    } else if (step === 'info') {
      setStep('series');
      setArchitectInfo(null);
    } else if (step === 'series') {
      setStep('readiness');
      setIsSeries(false);
    } else if (step === 'readiness') {
      setStep('medium');
      setMedium(null);
      setReadiness(null);
    }
  };

  /**
   * Skip onboarding for returning users who already have projects.
   */
  const handleSkip = () => {
    completeOnboarding();
    if (completeOnboardingFlow) {
      completeOnboardingFlow(null, null);
    }
    navigate('/chat');
  };

  if (isLoading) {
    return (
      <div className="onboarding-page">
        <div className="onboarding-page__loading">
          <p>Setting up your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-page">
      <button
        className="onboarding-page__theme-toggle"
        onClick={toggleTheme}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        )}
      </button>

      {error && (
        <div className="onboarding-page__error">
          {error}
        </div>
      )}

      {step === 'medium' && (
        <>
          <MediumSelector onSelect={handleMediumSelect} />
          <button
            onClick={handleSkip}
            className="onboarding-page__skip-button"
          >
            I already have a project → Skip
          </button>
        </>
      )}

      {step === 'readiness' && (
        <>
          <ReadinessSelector
            medium={medium}
            onSelect={handleReadinessSelect}
          />
          <button
            onClick={handleBack}
            className="onboarding-page__back-button"
          >
            ← Back
          </button>
        </>
      )}

      {step === 'series' && (
        <>
          <SeriesQuestion
            medium={medium}
            onSelect={handleSeriesSelect}
          />
          <button
            onClick={handleBack}
            className="onboarding-page__back-button"
          >
            ← Back
          </button>
        </>
      )}

      {step === 'info' && (
        <>
          <BasicInfoForm
            medium={medium}
            onSubmit={handleInfoSubmit}
          />
          <button
            onClick={handleBack}
            className="onboarding-page__back-button"
          >
            ← Back
          </button>
        </>
      )}

      {step === 'template' && (
        <div className="onboarding-page__template-step">
          <TemplatePicker
            templates={templates}
            onSelect={handleTemplateSelect}
            isFirstSession={true}
          />
          <button
            onClick={handleBack}
            className="onboarding-page__back-button"
          >
            ← Back
          </button>
        </div>
      )}

      {step === 'naming' && (
        <div className="onboarding-page__naming-step">
          <SessionNamingStep
            template={selectedTemplate}
            onBack={handleBack}
            onSubmit={handleSessionNameSubmit}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
}

export default OnboardingPage;
